import type { ActionResult, JourneyKind, NavigationReason, ParticipationOperation, ParticipationOutcome, ParticipationStep } from "./state";

export type ParticipationProperties = {
  schema_version: 1;
  flow_id: string;
  journey_kind: JourneyKind;
  event_id: string;
  step?: ParticipationStep;
  is_group?: boolean;
  ticket_count?: number;
  approval_required?: boolean;
  is_test_run?: boolean;
};
export type ParticipationEventMap = {
  participation_started: ParticipationProperties & { entry_point: "plan" | "claim_link" };
  participation_resumed: ParticipationProperties & { resume_reason: "reopened" };
  participation_step_viewed: ParticipationProperties & { visit_id: string; navigation_reason: NavigationReason };
  participation_action_started: ParticipationProperties & { operation: ParticipationOperation; operation_id: string; attempt_number: number };
  participation_action_finished: ParticipationProperties & { operation: ParticipationOperation; operation_id: string; result: ActionResult; duration_ms: number; error_category?: "validation" | "network" | "unavailable" | "session" | "unknown" };
  participation_closed: ParticipationProperties & { close_reason: "dismissed" };
  participation_completed: ParticipationProperties & { outcome: ParticipationOutcome };
};
export type ParticipationCapture = <E extends keyof ParticipationEventMap>(event: E, properties: ParticipationEventMap[E]) => void;
type Journal = { flowId: string; completed: ParticipationOutcome[] };

export function participationJournalKey(scope: string, kind: JourneyKind) {
  return `participation:v1:${kind}:${scope}`;
}

// This record carries correlation only. It never contains a credential, form,
// identity token, payment secret or authority to resume a purchase.
export class ParticipationAnalytics {
  private journal: Journal;
  private step?: ParticipationStep;
  private closed = false;
  private attempts: Partial<Record<ParticipationOperation, number>> = {};
  private key: string;
  constructor(
    scope: string,
    private facts: Omit<ParticipationProperties, "schema_version" | "flow_id" | "step">,
    private capture: ParticipationCapture,
    private storage?: Pick<Storage, "getItem" | "setItem"> & Partial<Pick<Storage, "removeItem">>,
    private newID = () => crypto.randomUUID(),
    newPurchase = false,
  ) {
    this.key = participationJournalKey(scope, facts.journey_kind);
    let saved: Journal | undefined;
    try {
      const value = JSON.parse(storage?.getItem(this.key) ?? "null");
      if (typeof value?.flowId === "string" && /^[a-zA-Z0-9-]{1,100}$/.test(value.flowId) && Array.isArray(value.completed)) {
        saved = { flowId: value.flowId, completed: value.completed.filter((outcome: unknown) => ["request_sent", "booking_confirmed", "recipient_details_completed", "app_handoff"].includes(String(outcome))) };
      }
    } catch { /* Analytics storage must never affect participation. */ }
    if (newPurchase && saved?.completed.length) saved = undefined;
    this.journal = saved ?? { flowId: newID(), completed: [] };
    this.persist();
    if (!saved) this.emit("participation_started", { entry_point: facts.journey_kind === "claim" ? "claim_link" : "plan" });
    else this.emit("participation_resumed", { resume_reason: "reopened" });
  }
  update(facts: typeof this.facts) { this.facts = facts; }
  move(scope: string) {
    try { this.storage?.removeItem?.(this.key); } catch { /* Best effort. */ }
    this.key = participationJournalKey(scope, this.facts.journey_kind);
    this.persist();
  }
  view(step: ParticipationStep, reason: NavigationReason) {
    if (this.closed) {
      this.closed = false;
      this.step = undefined;
      if (step === "tickets" && this.journal.completed.length) {
        this.journal = { flowId: this.newID(), completed: [] };
        this.persist();
        this.emit("participation_started", { entry_point: "plan" });
      } else this.emit("participation_resumed", { resume_reason: "reopened" });
    }
    if (this.step === step) return;
    this.step = step;
    this.emit("participation_step_viewed", { visit_id: this.newID(), navigation_reason: reason });
  }
  start(operation: ParticipationOperation) {
    const id = this.newID();
    this.attempts[operation] = (this.attempts[operation] ?? 0) + 1;
    const startedAt = Date.now();
    const step = this.step;
    this.emit("participation_action_started", { operation, operation_id: id, attempt_number: this.attempts[operation]! });
    let finished = false;
    return {
      id,
      finish: (result: ActionResult, error_category?: ParticipationEventMap["participation_action_finished"]["error_category"]) => {
        if (finished) return;
        finished = true;
        this.emit("participation_action_finished", { operation, operation_id: id, result, error_category, duration_ms: Math.max(0, Date.now() - startedAt) }, step);
      },
    };
  }
  complete(outcome: ParticipationOutcome) {
    try {
      const saved = JSON.parse(this.storage?.getItem(this.key) ?? "null");
      if (saved?.flowId === this.journal.flowId && saved.completed?.includes(outcome)) return;
    } catch { /* Best effort. */ }
    if (this.journal.completed.includes(outcome)) return;
    this.journal.completed.push(outcome);
    this.persist();
    this.emit("participation_completed", { outcome });
  }
  close() { if (!this.closed) this.emit("participation_closed", { close_reason: "dismissed" }); this.closed = true; }
  private persist() {
    try { this.storage?.setItem(this.key, JSON.stringify(this.journal)); } catch { /* Best effort. */ }
  }
  private emit<E extends keyof ParticipationEventMap>(event: E, details: Omit<ParticipationEventMap[E], keyof ParticipationProperties>, step = this.step) {
    // Enumerate common properties instead of spreading caller-owned objects.
    const { journey_kind, event_id, is_group, ticket_count, approval_required, is_test_run } = this.facts;
    try {
      this.capture(event, { schema_version: 1, flow_id: this.journal.flowId, journey_kind, event_id, is_group, ticket_count, approval_required, is_test_run, step, ...details } as ParticipationEventMap[E]);
    } catch { /* Blocked or broken telemetry cannot interrupt a booking. */ }
  }
}
